/**
 * Strip a phone string to its 10-digit US form.
 * Removes non-digit characters and strips a leading "1" country code.
 */
export function normalizePhoneDigits(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits;
}

/**
 * True when the value contains a complete US phone number — exactly 10 digits,
 * optionally with a leading "1" country code.
 */
export function isValidUsPhone(value: string): boolean {
  return normalizePhoneDigits(value).length === 10;
}

/**
 * Format a phone number for live "as-you-type" display in an input.
 * Tolerates partial input, progressively building up (XXX) XXX-XXXX.
 */
export function formatPhoneDisplay(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Format phone number to (XXX) XXX-XXXX or +1 (XXX) XXX-XXXX format
 * Supports both 10-digit numbers and 11-digit numbers with +1 country code
 */
export function formatPhoneNumber(phoneNumber?: string): string | undefined {
  if (!phoneNumber) return phoneNumber;

  // Extract digits only
  const digits = phoneNumber.replace(/\D/g, '');

  // Normalize to 10-digit US number
  let normalizedDigits: string;
  let hasCountryCode = false;

  if (digits.length === 11 && digits.startsWith('1')) {
    // 11 digits starting with 1 - strip country code
    normalizedDigits = digits.slice(1);
    hasCountryCode = true;
  } else if (digits.length === 10) {
    // 10 digits - use as is
    normalizedDigits = digits;
    // Check if original input had +1 prefix
    hasCountryCode = phoneNumber.includes('+1');
  } else {
    // Invalid length - return original
    return phoneNumber;
  }

  // Format the 10-digit number
  const formatted = `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6, 10)}`;

  // Add country code if present in original
  return hasCountryCode ? `+1 ${formatted}` : formatted;
}

/**
 * Build a tel: URI for a stored phone value. Dials the bare digits — stored
 * values carry formatting that would break the URI. Callers should only
 * linkify a value that passes isValidUsPhone; anything else has no dialable
 * number to hand the dialer.
 */
export function phoneTelUri(phone: string): string {
  return `tel:${normalizePhoneDigits(phone)}`;
}
